<template>
  <AppModal
    :is-open="isOpen"
    title="Rentz"
    subtitle="Tap players in the order they finished."
    @close="emit('close')"
  >
    <template #leading>
      <GameIndex symbol="R" />
    </template>
    <div class="players">
      <button
        v-for="row in rows"
        :key="row.id"
        type="button"
        class="player-option"
        :class="{ 'is-placed': row.place, 'is-auto': row.isAuto }"
        :aria-label="row.label"
        :disabled="row.isAuto"
        @click="togglePlayer(row.id)"
      >
        <span class="place" aria-hidden="true">{{ row.place ? ordinal(row.place) : '' }}</span>
        <span class="name">{{ row.name }}</span>
        <span v-if="row.place" class="points">{{ formatPoints(row.points) }}</span>
      </button>
    </div>
    <template #footer>
      <p class="status" aria-live="polite">{{ statusText }}</p>
      <div class="actions">
        <button
          v-if="order.length"
          type="button"
          class="btn btn-secondary start-over"
          @click="order = []"
        >
          Start over
        </button>
        <button
          type="button"
          class="btn btn-ink save"
          :disabled="!isComplete"
          @click="handleConfirm"
        >
          Save round
        </button>
      </div>
    </template>
  </AppModal>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { GAME_TYPES } from '../constants/gameTypes';
import { useGameConfigStore } from '../stores/gameConfig';
import { formatPoints } from '../utils/scores';
import AppModal from './AppModal.vue';
import GameIndex from './GameIndex.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  players: {
    type: Array,
    required: true
  }
});

const emit = defineEmits(['confirm', 'close']);

const gameConfig = useGameConfigStore();
const positionPoints = computed(() =>
  gameConfig.games[GAME_TYPES.RENTZ].getPositionPoints(props.players.length)
);

const order = ref([]);

watch([() => props.isOpen, () => props.players], () => (order.value = []), { deep: true });

const ordinal = (n) => {
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n] ?? 'th';
  return `${n}${suffix}`;
};

const autoId = computed(() => {
  if (order.value.length !== props.players.length - 1) return null;
  return props.players.find((player) => !order.value.includes(player.id))?.id ?? null;
});

const finalOrder = computed(() => (autoId.value ? [...order.value, autoId.value] : order.value));

const isComplete = computed(
  () => props.players.length > 0 && finalOrder.value.length === props.players.length
);

const rows = computed(() =>
  props.players.map((player) => {
    const index = finalOrder.value.indexOf(player.id);
    const place = index === -1 ? null : index + 1;
    const isAuto = player.id === autoId.value;
    const points = place ? (positionPoints.value[index] ?? 0) : 0;
    let label = `${player.name}, not placed yet`;
    if (isAuto) label = `${player.name}, finished last`;
    else if (place) label = `${player.name}, finished ${ordinal(place)}. Tap to remove`;
    return { id: player.id, name: player.name, place, isAuto, points, label };
  })
);

const statusText = computed(() => {
  if (isComplete.value) return 'Finishing order complete';
  return `Tap who finished ${ordinal(order.value.length + 1)}`;
});

const togglePlayer = (id) => {
  order.value = order.value.includes(id)
    ? order.value.filter((placed) => placed !== id)
    : [...order.value, id];
};

const handleConfirm = () => {
  if (!isComplete.value) return;
  emit(
    'confirm',
    finalOrder.value.map((playerId, index) => ({ playerId, score: positionPoints.value[index] }))
  );
};
</script>

<style scoped>
.players {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.player-option {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  min-height: 56px;
  padding: 0 1rem 0 0.625rem;
  border-radius: var(--radius-control);
  border: 1px solid var(--card-line);
  background-color: var(--control);
  color: var(--ink);
  text-align: left;
  font-weight: 500;
  user-select: none;
  -webkit-user-select: none;
  transition:
    border-color 0.15s,
    background-color 0.15s;
}

.player-option:hover:not(:disabled) {
  border-color: var(--ink-muted);
}

.player-option:disabled {
  cursor: default;
}

.place {
  flex: none;
  width: 2.5rem;
  height: 2.5rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  border: 2px dashed var(--card-line);
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.0625rem;
  line-height: 1;
}

.is-placed .place {
  border: none;
  background-color: var(--strong);
  color: var(--on-strong);
}

.is-placed {
  border-color: var(--strong);
}

.is-auto .place {
  background-color: transparent;
  border: 2px dashed var(--ink-muted);
  color: var(--ink);
}

.is-auto {
  border-style: dashed;
  border-color: var(--card-line);
}

.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.points {
  flex: none;
  font-weight: 600;
  font-size: var(--step--1);
  color: var(--ink-muted);
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.start-over {
  flex: none;
}

.save {
  flex: 1;
}
</style>
